import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { FileSignature, CheckCircle2, AlertCircle, Eraser, Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [signerName, setSignerName] = useState('');
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedOk, setSignedOk] = useState(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/contracts/public/${token}/info`);
        if (cancelled) return;
        setInfo(r.data);
        if (r.data.client_name) setSignerName(r.data.client_name);
        if (r.data.status === 'signed') setSignedOk({ signedAt: r.data.signed_at, name: r.data.signer_name });
      } catch (e) {
        setErrorMsg(e.response?.data?.detail || 'Link non valido o scaduto');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  // Canvas drawing
  const startDraw = (e) => {
    const c = canvasRef.current; if (!c) return;
    drawingRef.current = true;
    dirtyRef.current = true;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = c.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDraw = () => { drawingRef.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    dirtyRef.current = false;
  };

  const sign = async () => {
    if (!signerName.trim()) { toast.error('Inserisci il tuo nome'); return; }
    if (!accept) { toast.error('Devi accettare per firmare'); return; }
    if (!dirtyRef.current) { toast.error('Disegna la tua firma nel riquadro'); return; }
    const c = canvasRef.current;
    const dataUrl = c.toDataURL('image/png');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contracts/public/${token}/sign`, {
        signer_name: signerName.trim(),
        signature_data_url: dataUrl,
        accept_terms: true,
      });
      setSignedOk({ signedAt: new Date().toISOString(), name: signerName });
      toast.success('Contratto firmato con successo!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore firma');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-700" /></div>;
  }
  if (errorMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white border border-red-200 rounded-lg p-8 max-w-md text-center" data-testid="contract-error">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold mb-2">Link non valido</h1>
          <p className="text-slate-600">{errorMsg}</p>
        </div>
      </div>
    );
  }
  if (signedOk) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-6">
        <div className="bg-white border border-emerald-200 rounded-lg p-8 max-w-md text-center" data-testid="contract-signed-ok">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-3" />
          <h1 className="text-2xl font-bold mb-2 text-emerald-800">Contratto firmato!</h1>
          <p className="text-slate-700 mb-1"><strong>{signedOk.name || signerName}</strong></p>
          <p className="text-xs text-slate-500">{new Date(signedOk.signedAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          <p className="text-sm text-slate-600 mt-4">Una copia firmata è stata salvata e inviata allo studio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h1 className="text-2xl font-bold flex items-center gap-2 text-amber-800">
            <FileSignature className="w-6 h-6" /> Firma elettronica contratto
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            <strong>{info?.studio_name || 'Lo studio'}</strong> ti ha inviato il documento
            <strong> &quot;{info?.title}&quot;</strong> da firmare.
          </p>
        </div>

        {/* PDF preview */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="bg-slate-100 px-4 py-2 text-xs font-medium text-slate-600 border-b">Anteprima documento</div>
          <iframe
            title="contratto"
            src={`${API_URL}/api/contracts/public/${token}/pdf`}
            className="w-full"
            style={{ height: '600px', border: 'none' }}
            data-testid="contract-pdf-preview"
          />
        </div>

        {/* Signature form */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
          <h2 className="text-lg font-semibold">La tua firma</h2>
          <div>
            <Label>Nome e cognome del firmatario *</Label>
            <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="es. Mario Rossi" data-testid="signer-name" />
          </div>
          <div>
            <Label>Disegna la tua firma nel riquadro sotto *</Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-slate-50 mt-1">
              <canvas
                ref={canvasRef}
                width={760}
                height={200}
                className="bg-white w-full touch-none cursor-crosshair"
                style={{ maxWidth: '100%' }}
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
                data-testid="signature-canvas"
              />
            </div>
            <div className="flex justify-end mt-2">
              <Button size="sm" variant="ghost" onClick={clearCanvas} data-testid="clear-signature">
                <Eraser className="w-3.5 h-3.5 mr-1" /> Cancella
              </Button>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-3">
            <Checkbox id="accept" checked={accept} onCheckedChange={setAccept} data-testid="accept-checkbox" />
            <label htmlFor="accept" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
              Dichiaro di aver letto e accettato il contenuto del documento. Apponendo la mia firma autografa
              elettronica, accetto i termini riportati e autorizzo il trattamento dei dati e la registrazione
              di timestamp e indirizzo IP a fini di validità legale (Reg. UE 910/2014 — firma elettronica semplice).
            </label>
          </div>
          <Button
            onClick={sign}
            disabled={submitting}
            className="w-full bg-amber-700 hover:bg-amber-800"
            data-testid="submit-signature"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio…</> : <><Send className="w-4 h-4 mr-2" />Firma il contratto</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
